import { esLiteral, useEsStrings } from '../../common/EsStrings';
import { useEsLocale } from '../../common/EsLocale';

/**
 * The birthdate, shared by the register page and the birthdate step so the two ask the same
 * way: day, month, and year as three boxes, in the order and with the month names and field
 * labels of the reader's locale (Tim, 2026-10-14, "make date format i18n aware").
 *
 * <p>A NEUTRAL AGE SCREEN (FTC COPPA FAQ D.7, H.3): the legend is "Birthdate", nothing beside
 * it names an age or says what one changes, and every box starts empty. The age rule is
 * esback's alone (EsAge); this page never checks MIN_AGE, so nothing here hints at it.
 *
 * <p>The value is the three boxes as typed, "year-month-day" with any of them empty
 * ("1990-1-5", "-3-"), so a half-typed date survives a re-render; esBirthdateIso turns a whole
 * one into what esback reads. The month box's option values are 1..12 whatever the locale, so
 * a test sets a date by testid and value without knowing the language (EsAccountActions).
 */

// The app's language, with the browser's region when the browser speaks that language: en
// and an en-GB browser write day first, en and en-US month first.
export function esDateLocale( pAppLocale, pBrowser = typeof navigator === 'undefined' ? null : navigator.language ) {
    const aApp = pAppLocale || 'en';
    const aLanguage = ( pTag ) => pTag.toLowerCase().split( /[-_]/ )[ 0 ];
    return pBrowser && aLanguage( pBrowser ) === aLanguage( aApp ) ? pBrowser : aApp;
}

const SAMPLE = new Date( Date.UTC( 2000, 10, 22 ) );

/** day, month, and year in the order this locale writes a date. */
export function esDateOrder( pLocale ) {
    try {
        const aOrder = new Intl.DateTimeFormat( pLocale, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' } )
            .formatToParts( SAMPLE )
            .map( ( pPart ) => pPart.type )
            .filter( ( pType ) => pType === 'day' || pType === 'month' || pType === 'year' );
        return aOrder.length === 3 ? aOrder : [ 'month', 'day', 'year' ];
    }
    catch {
        return [ 'month', 'day', 'year' ];
    }
}

/** The twelve month names in this locale, January first. */
export function esMonthNames( pLocale ) {
    const aFormat = new Intl.DateTimeFormat( pLocale, { month: 'long', timeZone: 'UTC' } );
    return Array.from( { length: 12 }, ( pUnused, pIndex ) => aFormat.format( new Date( Date.UTC( 2000, pIndex, 1 ) ) ) );
}

const ENGLISH = { day: 'Day', month: 'Month', year: 'Year' };

/** "Day", "Month", or "Year" in this locale, capitalised; English where Intl has no name. */
export function esFieldLabel( pLocale, pField ) {
    try {
        const aName = new Intl.DisplayNames( [ pLocale ], { type: 'dateTimeField' } ).of( pField );
        if ( aName ) return aName.charAt( 0 ).toLocaleUpperCase( pLocale ) + aName.slice( 1 );
    }
    catch {
        // An engine without dateTimeField names.
    }
    return ENGLISH[ pField ];
}

// The month as the box's option value: "01" off an ISO date is option "1".
export const esBirthdateParts = ( pValue ) => {
    const [ aYear = '', aMonth = '', aDay = '' ] = String( pValue ?? '' ).split( '-' );
    return { year: aYear, month: /^\d+$/.test( aMonth ) ? String( Number( aMonth ) ) : aMonth, day: aDay };
};

const pad = ( pN ) => String( pN ).padStart( 2, '0' );

/** The value as YYYY-MM-DD when it is a whole, real date, else null. */
export function esBirthdateIso( pValue ) {
    const { year, month, day } = esBirthdateParts( pValue );
    if ( !/^\d{4}$/.test( year ) || !/^\d{1,2}$/.test( month ) || !/^\d{1,2}$/.test( day ) ) return null;

    const aDate = new Date( Date.UTC( Number( year ), Number( month ) - 1, Number( day ) ) );
    const aReal = aDate.getUTCFullYear() === Number( year ) && aDate.getUTCMonth() === Number( month ) - 1
        && aDate.getUTCDate() === Number( day );
    return aReal ? `${ year }-${ pad( month ) }-${ pad( day ) }` : null;
}

const todayIso = ( pToday ) => `${ pToday.getFullYear() }-${ pad( pToday.getMonth() + 1 ) }-${ pad( pToday.getDate() ) }`;

/** What only a typo could produce: the same refusals esback gives, never an age. */
export function esBirthdateRefusal( pValue, pToday = new Date(), t = esLiteral ) {
    const { year, month, day } = esBirthdateParts( pValue );
    if ( !year && !month && !day ) return t( 'account-birthdate-required', 'Birthdate is required.' );

    const aIso = esBirthdateIso( pValue );
    if ( !aIso ) return t( 'account-birthdate-not-real', 'Enter a real date: day, month, and year.' );
    if ( aIso > todayIso( pToday ) ) return t( 'account-birthdate-future', 'That birthdate is in the future.' );
    if ( Number( year ) < pToday.getFullYear() - 120 ) return t( 'account-birthdate-too-old', 'That birthdate is more than {years} years ago.', { years: '120' } );
    return null;
}

const BOX = 'w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-4 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus';

export default function EsBirthdateFields( { idPrefix, bday, onChange, autoComplete } ) {
    const t = useEsStrings();
    const aLocale = esDateLocale( useEsLocale() );
    const aParts = esBirthdateParts( bday );

    const change = ( pField ) => ( pEvent ) => {
        const aNext = { ...aParts, [ pField ]: pEvent.target.value.replace( /\D/g, '' ) };
        onChange( aNext.year || aNext.month || aNext.day ? `${ aNext.year }-${ aNext.month }-${ aNext.day }` : '' );
    };

    const aId = ( pField ) => `${ idPrefix }-bday-${ pField }`;

    const aBoxes = {
        day: <input type="text" inputMode="numeric" maxLength={ 2 } autoComplete={ autoComplete ?? 'bday-day' }
                    id={ aId( 'day' ) } data-testid={ aId( 'day' ) }
                    value={ aParts.day } onChange={ change( 'day' ) } className={ BOX } />,
        // The browser's own arrow sits against the right border whatever the padding (B20), so
        // the box draws none and a chevron of the page's is inset as far as the text is.
        month: <div className="relative">
                   <select id={ aId( 'month' ) } data-testid={ aId( 'month' ) } autoComplete={ autoComplete ?? 'bday-month' }
                           value={ aParts.month } onChange={ change( 'month' ) } className={ `${ BOX } appearance-none pr-10` }>
                       <option value="" />
                       { esMonthNames( aLocale ).map( ( pName, pIndex ) =>
                           <option key={ pIndex } value={ String( pIndex + 1 ) }>{ pName }</option> ) }
                   </select>
                   <i className="pi pi-chevron-down pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-body"
                      aria-hidden="true" />
               </div>,
        year: <input type="text" inputMode="numeric" maxLength={ 4 } autoComplete={ autoComplete ?? 'bday-year' }
                     id={ aId( 'year' ) } data-testid={ aId( 'year' ) }
                     value={ aParts.year } onChange={ change( 'year' ) } className={ BOX } />,
    };
    const aWidths = { day: 'w-20', month: 'grow', year: 'w-28' };

    return (
        <fieldset data-testid={ `${ idPrefix }-bday` } className="mb-8">
            <legend className="mb-3 block text-sm font-medium text-heading">
                { t( 'account-birthdate-label', 'Birthdate' ) }
            </legend>
            <div className="flex gap-3">
                { esDateOrder( aLocale ).map( ( pField ) =>
                    <div key={ pField } className={ aWidths[ pField ] }>
                        <label htmlFor={ aId( pField ) } className="mb-1 block text-xs text-aside">
                            { esFieldLabel( aLocale, pField ) }
                        </label>
                        { aBoxes[ pField ] }
                    </div> ) }
            </div>
        </fieldset>
    );
}
